import { memo, useCallback, useMemo, useRef, useState } from 'react';
import { View } from 'react-native';
import { scheduleOnRN } from 'react-native-worklets';
import {
  cancelAnimation,
  useSharedValue,
  withSequence,
} from 'react-native-reanimated';
import { useTranslation } from '@bonanza-social/i18n';
import {
  BottomSheet,
  StackNavigationProp,
  useNavigation,
} from '@bonanza-social/navigation';
import {
  useDispatch,
  useSelector,
  selectTokenName,
  selectTokenBalance,
} from '@bonanza-social/toolkit/state';
import { HapticFeedback } from '@bonanza-social/toolkit/utils';
import { Box, Toast, useBreakpointProp } from '@bonanza-social/moonlight';
import { selectPrizeWallCampaignEnded } from '../../state/prizeWallCampaign/prizeWallCampaign.slice';
import {
  selectPrizeWallState,
  selectPrizeWallPrizes,
  fetchPrizeWallReward,
  selectPrizeWallCostOfPlay,
  decreaseQuantity,
  setLockedRewardId,
  prizeWallCashRewardWon,
  prizeWallCoinsRewardWon,
  selectPrizeWallEligibilityError,
} from '../../state/prizeWall/prizeWall.slice';
import {
  findPosition,
  getAvailablePositions,
  groupPrizesByRows,
  shufflePositions,
  runMainAnimation,
  moveInstantly,
  fadeOut,
  fadeIn,
} from './PrizeWallPrizes.utils';
import {
  NONE_POSITION,
  MISS_POSITION,
  ITEMS_PER_ROW,
  ANIMATION_TILES_COUNT,
  QUICK_MISS_TILES_COUNT,
} from './PrizeWallPrizes.content';
import { shake } from './PrizeWallShake/PrizeWallShake.utils';
import { EligibilityError } from '../../state/prizeWall/prizeWall.types';
import { PrizeWallCampaignNavigatorParamsList } from '../../navigators/PrizeWallCampaignNavigator/PrizeWallCampaignNavigator.types';
import { PrizeWallPrizesProps } from './PrizeWallPrizes.types';
import PrizeWallPrize from './PrizeWallPrize/PrizeWallPrize';
import PrizeWallMiss from '../PrizeWallMiss/PrizeWallMiss';
import PrizeWallButton from '../PrizeWallButton/PrizeWallButton';
import PrizeWallShake from './PrizeWallShake/PrizeWallShake';

function PrizeWallPrizes({
  onInsufficientBalancePress,
  ...rest
}: PrizeWallPrizesProps) {
  const [t] = useTranslation('prizeWall');
  const [animating, setAnimating] = useState(false);
  const [awardedPrizePosition, setAwardedPrizePosition] = useState<number>();
  const navigation =
    useNavigation<StackNavigationProp<PrizeWallCampaignNavigatorParamsList>>();
  const dispatch = useDispatch();
  const state = useSelector(selectPrizeWallState);
  const prizes = useSelector(selectPrizeWallPrizes);
  const costOfPlay = useSelector(selectPrizeWallCostOfPlay);
  const campaignEnded = useSelector(selectPrizeWallCampaignEnded);
  const tokenName = useSelector(selectTokenName);
  const tokenBalance = useSelector(selectTokenBalance);
  const eligibilityError = useSelector(selectPrizeWallEligibilityError);
  const hasInsufficientBalance = (tokenBalance ?? 0) < (costOfPlay ?? 0);
  const itemsPerRow = useBreakpointProp(ITEMS_PER_ROW);
  const animatedValue = useSharedValue<number>(NONE_POSITION);
  const shakeAnimatedValue = useSharedValue<number>(0);
  const groupedPrizes = useMemo(
    () => groupPrizesByRows(prizes, itemsPerRow),
    [prizes, itemsPerRow],
  );
  const awardedPrizeRef = useRef<View>(null);
  const awardedPrizeId = prizes?.ids?.[awardedPrizePosition ?? MISS_POSITION];
  const awardedPrize = awardedPrizeId
    ? prizes?.byId?.[awardedPrizeId]
    : undefined;

  const fetchReward = useCallback(
    () =>
      new Promise<number>((resolve) => {
        dispatch(fetchPrizeWallReward()).then((response) => {
          if (fetchPrizeWallReward.fulfilled.match(response)) {
            const { isWin, rewardId } = response.payload;

            if (isWin && rewardId !== undefined) {
              const awardedPrizePosition =
                findPosition(prizes, rewardId) ?? MISS_POSITION;

              setAwardedPrizePosition(awardedPrizePosition);

              resolve(awardedPrizePosition);
            } else {
              setAwardedPrizePosition(MISS_POSITION);

              resolve(MISS_POSITION);
            }
          }

          if (fetchPrizeWallReward.rejected.match(response)) {
            setAwardedPrizePosition(MISS_POSITION);

            resolve(MISS_POSITION);
          }
        });
      }),
    [prizes, dispatch],
  );

  const handleInsufficientBalance = useCallback(() => {
    Toast.show({
      type: 'custom',
      title: t('prizeWallInsufficientBalanceTitle', { tokenName }),
      text: t('prizeWallInsufficientBalanceSubtitle'),
      props: {
        trailingIconProps: {
          name: 'arrowForward',
        },
      },
      onPress: () => {
        onInsufficientBalancePress?.();

        Toast.hide();
      },
    });
  }, [tokenName, onInsufficientBalancePress, t]);

  const handleMiss = useCallback(
    (currentPosition: number) => {
      if (currentPosition === MISS_POSITION) {
        setAnimating(false);

        shakeAnimatedValue.value = shake();

        return;
      }

      animatedValue.value = withSequence(
        fadeOut(currentPosition, () => setAnimating(false)),
        moveInstantly(MISS_POSITION),
        fadeIn(MISS_POSITION, () => (shakeAnimatedValue.value = shake())),
      );
    },
    [animatedValue, shakeAnimatedValue],
  );

  const handleReward = useCallback(
    ([currentPosition, awardedPrizePosition]: [number, number]) => {
      const handle = () => {
        const awardedPrizeId = prizes?.ids[awardedPrizePosition];
        const awardedPrize = awardedPrizeId
          ? prizes?.byId[awardedPrizeId]
          : undefined;

        if (awardedPrizeId) {
          dispatch(decreaseQuantity(awardedPrizeId));

          awardedPrizeRef.current?.measureInWindow((x, y, width, height) => {
            navigation.navigate('PrizeWallAwardScreen', {
              rewardId: awardedPrizeId,
              glowX: x + width / 2,
              glowY: y + height / 2,
            });
          });
        }

        if (awardedPrize?.type === 'Cash') {
          dispatch(prizeWallCashRewardWon());
        }

        if (awardedPrize?.type === 'Coins') {
          dispatch(prizeWallCoinsRewardWon());
        }

        dispatch(setLockedRewardId(undefined));
      };

      // The awarded prize is already highlighted
      if (currentPosition === awardedPrizePosition) {
        setAnimating(false);

        handle();

        return;
      }

      // Hightlight the reward box on the wall
      animatedValue.value = withSequence(
        fadeOut(currentPosition, () => setAnimating(false)),
        moveInstantly(awardedPrizePosition),
        fadeIn(awardedPrizePosition, () => {
          HapticFeedback.trigger('impactMedium');

          handle();
        }),
      );
    },
    [prizes, animatedValue, navigation, dispatch],
  );

  const handleEligibilityError = useCallback((error: EligibilityError) => {
    BottomSheet.open({
      namespace: 'common',
      type: 'InfoImage',
      data: {
        image: 'common.rejected',
        titleNS: 'common',
        titleKey: 'errorTitle',
        descriptionNS: 'prizeWall',
        descriptionKey: `prizeWall${error}`,
      },
    });
  }, []);

  const handlePlay = useCallback(() => {
    if (eligibilityError !== undefined) {
      handleEligibilityError(eligibilityError);

      return;
    }

    cancelAnimation(animatedValue);

    setAnimating(true);

    const availablePositions = getAvailablePositions(prizes);
    const shuffledPositions = shufflePositions(availablePositions);
    const isQuickMiss = false; // Temporary disabled

    fetchReward().then((position) => (awardedPrizePosition = position));

    let animatedTilesCount = 0;
    let awardedPrizePosition: number;

    animatedValue.value = runMainAnimation(shuffledPositions, (position) => {
      animatedTilesCount++;

      // There is no awarded prize yet.
      if (awardedPrizePosition === undefined) {
        return;
      }

      const hasPrize = awardedPrizePosition !== MISS_POSITION;
      const showPrize = animatedTilesCount >= ANIMATION_TILES_COUNT;
      const showMiss =
        (isQuickMiss && animatedTilesCount >= QUICK_MISS_TILES_COUNT) ||
        showPrize;

      if (!hasPrize && showMiss) {
        cancelAnimation(animatedValue);
        scheduleOnRN(handleMiss, position);
      }

      if (hasPrize && showPrize) {
        cancelAnimation(animatedValue);
        scheduleOnRN(handleReward, [position, awardedPrizePosition]);
      }
    });
  }, [
    prizes,
    animatedValue,
    eligibilityError,
    handleEligibilityError,
    handleMiss,
    handleReward,
    fetchReward,
  ]);

  return (
    <Box flex={1} {...rest}>
      <Box flex={1} gap="space-200" paddingBottom="space-400">
        {groupedPrizes.map((prizes, rowIndex) => (
          <Box
            key={rowIndex}
            flexShrink={1}
            pointerEvents={animating ? 'none' : undefined}
            flexDirection="row"
            gap="space-200"
          >
            {prizes.map((prize, index) => (
              <PrizeWallPrize
                ref={
                  awardedPrize?.rewardId === prize.rewardId
                    ? awardedPrizeRef
                    : undefined
                }
                key={prize.rewardId}
                position={index + rowIndex * prizes.length}
                animatedValue={animatedValue}
                awarded={
                  awardedPrize?.rewardId === prize.rewardId && !animating
                }
                outOfStock={
                  prize.remainingQuantity <= 0 &&
                  (state === 'Open' || state === 'Finished')
                }
                quantityLocked={
                  animating && prize.rewardId === awardedPrize?.rewardId
                }
                quantityFormat={state === 'Pending' ? 'multiplier' : 'fraction'}
                {...prize}
              />
            ))}
          </Box>
        ))}
        <PrizeWallShake animatedValue={shakeAnimatedValue}>
          <PrizeWallMiss
            missed={awardedPrizePosition === MISS_POSITION && !animating}
            animatedValue={animatedValue}
          />
        </PrizeWallShake>
      </Box>
      <PrizeWallButton
        disabled={
          animating ||
          campaignEnded ||
          state === 'Pending' ||
          state === 'Finished'
        }
        onPress={
          hasInsufficientBalance ? handleInsufficientBalance : handlePlay
        }
      />
    </Box>
  );
}

export default memo(PrizeWallPrizes);
